import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {ConfirmDialog, confirmDialog} from 'primereact/confirmdialog'
import {ConfirmPopup, confirmPopup} from 'primereact/confirmpopup'
import {ContextMenu} from 'primereact/contextmenu'
import {DataTable} from 'primereact/datatable'
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import {ReactComponent as CurrentIcon} from '../../assets/icons/CurrentIcon.svg'
import {ReactComponent as LockRow} from '../../assets/icons/LockRow.svg'
import {ReactComponent as NotCurrentIcon} from '../../assets/icons/NotCurrentIcon.svg'
import {ReactComponent as TreeIcon} from '../../assets/icons/TreeIcon.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {actionTableKeys} from '../new-table/Constants'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {permissionOptionKeys} from '../role/RoleConstant'
import {useDatatableContext} from './DataTableContext'
import StyledTablePaginator from './StyledTablePaginator'
import {
    columnsVisibilityTypes,
    getColumnVisibility,
    getShouldRememberColumns,
    getShouldRememberTableLock,
    getTableLock,
    getTableLockedColumns,
    getTableLockedRows,
    storeColumnVisibility,
    storeColumnVisibilityByTableType,
    storeShouldRememberColumns,
    storeShouldRememberTableLock,
    storeTableLock,
    storeTableLockedColumns,
    storeTableLockedRows,
} from './StyledTableUtils'
import {
    columnDataTableEvent,
    columnEventTypes,
    getColumnVisibilityChangeEventKey,
    removeChangeColumnsViewedListener,
} from './broadcast/ColumnEvent'
import {
    getTableLockEventKey,
    onRefLockChangeListener,
    removeTableLockChangeListener,
    tableLockEvent,
    tableLockEventTypes,
} from './broadcast/TableLockEvent'

import {ProgressSpinner} from 'primereact/progressspinner'
import noRecordImg from '../../assets/images/No-Record.svg'

const defaultTableLock = {
    rowLock: false,
    columnLock: false,
}

const StyledDataTable = React.memo(
    ({contextMenu = {}, onRowSelect = () => {}, ...props}) => {
        useSignals()

        const {clearSelectedContextItem, ...contextMenuProps} = contextMenu
        const {translateDatatable, translateKeys, translateCommons} =
            TranslateUtils()

        //#region DataTable Context
        const {
            tableType,
            tableActions,
            dataKey,
            queryData,
            isFetched,
            columnList,
            expansionOption,
            pageable,
            onChangeSortColumn,
            onDetailRowClick,
            handleCheckPermissionByKey,
            handleAction,
            minHeightFullScreen,
            isTealBackground,
        } = useDatatableContext()

        const dataList = useMemo(() => {
            if (!queryData || !isArrayAndNotEmpty(queryData?.content)) {
                return []
            }

            return queryData.content
        }, [queryData])

        //#region Load From Storage or Default
        const tableColumnList = useMemo(() => {
            const didRememberSettings = getShouldRememberColumns(tableType)

            if (!didRememberSettings) {
                return [...columnList]
            }

            const storedColumnVisibility = getColumnVisibility(tableType)
            let mappedColumnList = columnList.map(column => {
                const storedColumn = storedColumnVisibility.find(
                    storedColumn => storedColumn.field === column.field,
                )

                if (!storedColumn) {
                    return {...column}
                }

                return {
                    ...column,
                    initialShown: storedColumn.initialShown,
                }
            })

            return mappedColumnList?.length > 0
                ? [...mappedColumnList]
                : [...columnList]
        }, [tableType, columnList])

        const tableLockConfig = useMemo(() => {
            const didRememberSettings = getShouldRememberTableLock(tableType)

            if (!didRememberSettings) {
                return {...defaultTableLock}
            }

            const storedTableLock = getTableLock(tableType)

            return storedTableLock && Object.keys(storedTableLock).length > 0
                ? {...storedTableLock}
                : {...defaultTableLock}
        }, [tableType])

        const tableLockColumnsDefaultValue = useMemo(() => {
            const didRememberSettings = getShouldRememberTableLock(tableType)
            if (!didRememberSettings) {
                return []
            }

            const storedTableLockColumns = getTableLockedColumns(tableType)

            return storedTableLockColumns.length > 0
                ? [...storedTableLockColumns]
                : []
        }, [tableType])

        const tableLockRowsDefaultValue = useMemo(() => {
            const didRememberSettings = getShouldRememberTableLock(tableType)
            if (!didRememberSettings) {
                return []
            }

            const storedTableLockRows = getTableLockedRows(tableType)

            return storedTableLockRows.length > 0
                ? [...storedTableLockRows]
                : []
        }, [tableType])
        //#endregion

        //#region Row Expansion
        const expandedRows = useSignal([])
        const handleRowToggle = e => {
            expandedRows.value = e.data
        }

        const rowExpansionProps = useMemo(() => {
            if (!expansionOption?.hasExpansion) {
                return {}
            }

            return {
                rowExpansionTemplate:
                    expansionOption?.rowExpansionTemplate || null,
                expandedRows: expandedRows.value || [],
                onRowToggle: handleRowToggle,
            }
        }, [expansionOption, expandedRows.value])
        //#endregion

        //#region Column Lock
        const tableLock = useSignal(tableLockConfig)
        const tableLockColumns = useSignal(tableLockColumnsDefaultValue)
        const tableLockRows = useSignal(tableLockRowsDefaultValue)

        const tableScrollProps = useMemo(() => {
            if (!tableLock.value.rowLock || tableLockRows.value?.length < 1) {
                return {}
            }

            return {
                // scrollable: true,
                // scrollHeight: '100%',
            }
        }, [tableLock.value.rowLock, tableLockRows.value])

        const calculateLeft = useCallback(index => {
            if (index === 0) {
                return 0
            }

            return index * 200
        }, [])

        const handleLockColumns = field => {
            if (tableLockColumns.value.includes(field)) {
                return
            }

            const lockedColumnsTmp = [...tableLockColumns.value, field]

            tableLockColumns.value = lockedColumnsTmp

            storeTableLockedColumns(tableType, lockedColumnsTmp)

            onRefLockChangeListener(
                tableType,
                tableLockEventTypes.REF_LOCK_CHANGE_COLUMNS,
                lockedColumnsTmp,
            )
        }

        const handleUnlockColumns = field => {
            if (!tableLockColumns.value.includes(field)) {
                return
            }

            const lockedColumnsTmp = tableLockColumns.value.filter(
                col => col !== field,
            )

            tableLockColumns.value = lockedColumnsTmp

            storeTableLockedColumns(tableType, lockedColumnsTmp)

            onRefLockChangeListener(
                tableType,
                tableLockEventTypes.REF_LOCK_CHANGE_COLUMNS,
                lockedColumnsTmp,
            )
        }

        const handleLockRows = row => {
            if (tableLockRows.value.includes(row)) {
                return
            }

            const lockRowsTmp = [...tableLockRows.value, row]

            tableLockRows.value = lockRowsTmp

            storeTableLockedRows(tableType, lockRowsTmp)

            onRefLockChangeListener(
                tableType,
                tableLockEventTypes.REF_LOCK_CHANGE_ROWS,
                lockRowsTmp,
            )
        }

        const handleUnlockRows = row => {
            if (!tableLockRows.value.includes(row)) {
                return
            }

            const lockRowsTmp = tableLockRows.value.filter(r => r !== row)

            tableLockRows.value = lockRowsTmp

            storeTableLockedRows(tableType, lockRowsTmp)

            onRefLockChangeListener(
                tableType,
                tableLockEventTypes.REF_LOCK_CHANGE_ROWS,
                lockRowsTmp,
            )
        }

        const handleOnRestoreTableLock = () => {
            tableLock.value = defaultTableLock

            tableLockColumns.value = []
            tableLockRows.value = []

            storeTableLock(tableType, defaultTableLock)
            storeTableLockedColumns(tableType, [])
            storeTableLockedRows(tableType, [])
            storeShouldRememberTableLock(tableType, false)
        }

        const handleOnChangeTableLock = newTableLockConfig => {
            if (newTableLockConfig) {
                tableLock.value = {
                    ...tableLock.value,
                    ...newTableLockConfig,
                }

                storeTableLock(tableType, newTableLockConfig)

                if (!newTableLockConfig.columnLock) {
                    tableLockColumns.value = []

                    storeTableLockedColumns(tableType, [])
                }

                if (!newTableLockConfig.rowLock) {
                    tableLockRows.value = []

                    storeTableLockedRows(tableType, [])
                }
            }
        }

        const handleOnDiscardLockedColumns = () => {
            tableLockColumns.value = []

            storeTableLockedColumns(tableType, [])
        }

        const handleOnDiscardLockedRows = () => {
            tableLockRows.value = []

            storeTableLockedRows(tableType, [])
        }

        useEffect(() => {
            tableLockEvent.on(
                `${getTableLockEventKey(
                    tableType,
                    tableLockEventTypes.TABLE_LOCK_CHANGE,
                )}`,
                (newTableLockConfig = {}) => {
                    handleOnChangeTableLock(newTableLockConfig)
                },
            )

            tableLockEvent.on(
                `${getTableLockEventKey(
                    tableType,
                    tableLockEventTypes.TABLE_LOCK_RESTORE_DEFAULT,
                )}`,
                () => {
                    handleOnRestoreTableLock()
                },
            )

            tableLockEvent.on(
                `${getTableLockEventKey(
                    tableType,
                    tableLockEventTypes.TABLE_LOCK_DISCARD_COLUMNS,
                )}`,
                () => {
                    handleOnDiscardLockedColumns()
                },
            )

            tableLockEvent.on(
                `${getTableLockEventKey(
                    tableType,
                    tableLockEventTypes.TABLE_LOCK_DISCARD_ROWS,
                )}`,
                () => {
                    handleOnDiscardLockedRows()
                },
            )

            return () => {
                removeTableLockChangeListener(
                    tableType,
                    tableLockEventTypes.TABLE_LOCK_CHANGE,
                )

                removeTableLockChangeListener(
                    tableType,
                    tableLockEventTypes.TABLE_LOCK_RESTORE_DEFAULT,
                )

                removeTableLockChangeListener(
                    tableType,
                    tableLockEventTypes.TABLE_LOCK_DISCARD_COLUMNS,
                )

                removeTableLockChangeListener(
                    tableType,
                    tableLockEventTypes.TABLE_LOCK_DISCARD_ROWS,
                )
            }
        }, [])
        //#endregion

        //#region Column Visibility
        const shownColumns = useSignal(
            tableColumnList.filter(column => column.initialShown),
        )
        const hiddenColumns = useSignal(
            tableColumnList.filter(column => !column.initialShown),
        )
        const actionMenuRef = useRef(null)

        const handleStoreColumnsByType = (
            shownColumns = [],
            hiddenColumns = [],
        ) => {
            storeColumnVisibilityByTableType(
                tableType,
                columnsVisibilityTypes.shown,
                shownColumns,
            )
            storeColumnVisibilityByTableType(
                tableType,
                columnsVisibilityTypes.hidden,
                hiddenColumns,
            )
        }

        const renderColumnHeader = useCallback(
            (field, header) => {
                if (!tableLock.value.columnLock) {
                    return <div className="capitalized">{header}</div>
                }

                return (
                    <div className="flex justify-content-between align-items-center">
                        <div
                            className="mx-2 lock-icon"
                            onClick={() =>
                                tableLockColumns.value?.includes(field)
                                    ? handleUnlockColumns(field)
                                    : handleLockColumns(field)
                            }>
                            <i
                                className={`pi cursor-pointer p-1 ${
                                    tableLockColumns.value?.includes(field)
                                        ? 'pi-lock'
                                        : 'pi-lock-open'
                                }`}></i>
                        </div>
                        <div className="capitalized">{header}</div>
                    </div>
                )
            },
            [tableLock.value.columnLock],
        )

        const confirm = useCallback(
            (event, rowData) => {
                confirmPopup({
                    target: event.currentTarget,
                    message: translateDatatable('Do you want to delete ?'),
                    icon: 'pi pi-info-circle',
                    acceptLabel: translateCommons('Yes'),
                    acceptClassName: 'p-button-danger',
                    rejectLabel: translateCommons('No'),
                    accept: () => {
                        handleAction(actionTableKeys.delete, rowData)
                    },
                })
            },
            [handleAction, translateCommons, translateDatatable],
        )

        const confirmDeleteActionInMenu = rowData => {
            confirmDialog({
                message: 'Do you want to delete this record?',
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                defaultFocus: 'reject',
                acceptClassName: 'p-button-danger',
                accept: () => {
                    handleAction(actionTableKeys.delete, rowData)
                },
            })
        }

        const generateActionList = rowData => {
            if (!tableActions || tableActions?.length < 1) {
                return []
            }

            let actionItemsList = []
            if (
                tableActions.includes(actionTableKeys.edit) &&
                handleCheckPermissionByKey(permissionOptionKeys.allowToEdit)
            ) {
                actionItemsList.push({
                    key: actionTableKeys.edit,
                    label: 'Edit',
                    icon: 'pi pi-pencil',
                    command: () => onDetailRowClick(rowData?.id),
                })
            }

            if (
                tableActions.includes(actionTableKeys.delete) &&
                handleCheckPermissionByKey(permissionOptionKeys.allowToDelete)
            ) {
                actionItemsList.push({
                    key: actionTableKeys.delete,
                    label: 'Delete',
                    icon: 'pi pi-trash',
                    command: () => confirmDeleteActionInMenu(rowData),
                })
            }

            return [
                {
                    label: 'Options',
                    items: [...actionItemsList],
                },
            ]
        }

        const renderActionTemplate = rowData => {
            if (rowData.emptyRow) {
                return
            }

            if (!tableActions || tableActions?.length < 1) {
                return
            }

            return (
                <div className="flex gap-1 align-items-center">
                    {!!tableActions.includes(actionTableKeys.delete) &&
                        handleCheckPermissionByKey(
                            permissionOptionKeys.allowToDelete,
                        ) && (
                            <Button
                                icon="pi pi-trash"
                                severity="danger"
                                className="p-button-danger p-button-text p-1"
                                onClick={e => confirm(e, rowData)}
                            />
                        )}
                </div>
            )
        }

        const actionBodyTemplate = useCallback(
            (actionList, rowData) => {
                return (
                    <div className="flex gap-1 align-items-center">
                        {!!actionList.includes(actionTableKeys.edit) &&
                            handleCheckPermissionByKey(
                                permissionOptionKeys.allowToEdit,
                            ) && (
                                <Button
                                    className="p-button-rounded "
                                    icon="pi pi-pencil"
                                    aria-label={translateKeys('Edit')}
                                    tooltip={translateKeys('Edit')}
                                    onClick={() =>
                                        handleAction(
                                            actionTableKeys.edit,
                                            rowData,
                                        )
                                    }
                                    tooltipOptions={{
                                        position: 'top',
                                    }}
                                />
                            )}
                        {!!actionList.includes(actionTableKeys.delete) &&
                            handleCheckPermissionByKey(
                                permissionOptionKeys.allowToDelete,
                            ) && (
                                <Button
                                    icon="pi pi-trash"
                                    severity="danger"
                                    className="p-button-rounded p-button-danger p-button-outlined "
                                    onClick={e => confirm(e, rowData)}
                                    tooltip={translateKeys('Delete')}
                                    tooltipOptions={{position: 'top'}}
                                />
                            )}
                    </div>
                )
            },
            [confirm, handleAction, handleCheckPermissionByKey, translateKeys],
        )

        const columns = useMemo(() => {
            const frozenCols = shownColumns.value.filter(
                col =>
                    !!tableLock.value.columnLock &&
                    tableLockColumns.value?.includes(col.field),
            )
            const normalCols = shownColumns.value.filter(
                col => !tableLockColumns.value?.includes(col.field),
            )

            return [
                ...frozenCols.map((col, index) => (
                    <Column
                        className="sticky-column capitalized"
                        key={col.field}
                        field={col.field}
                        header={renderColumnHeader(col.field, col.header)}
                        sortable
                        resizeable
                        body={col?.body}
                        frozen={true}
                        style={{
                            '--left-position': `${calculateLeft(index++)}px`,
                            minWidth: '200px',
                            maxWidth: '200px',
                        }}
                    />
                )),

                ...normalCols.map(col => {
                    return (
                        <Column
                            className={`${
                                col.field === 'email' ? '' : 'capitalized'
                            }`}
                            bodyClassName={'overflow-hidden'}
                            headerStyle={{
                                overflow: 'hidden',
                            }}
                            bodyStyle={{
                                overflow: 'hidden',
                            }}
                            key={col.field}
                            field={col.field}
                            header={renderColumnHeader(col.field, col.header)}
                            sortable
                            bodyClassName={rowData =>
                                rowData.emptyRow ? 'datatable-empty-row' : ''
                            }
                            resizeable
                            body={col?.body}
                            style={{
                                minWidth: '160px',
                            }}
                        />
                    )
                }),
            ]
        }, [
            shownColumns.value,
            tableLock.value.columnLock,
            tableLockColumns.value,
            renderColumnHeader,
            calculateLeft,
            actionBodyTemplate,
        ])

        const handleRememberColumns = (
            newShownColumns,
            newHiddenColumns,
            columnList,
        ) => {
            const shouldRememberColumns = getShouldRememberColumns(tableType)
            if (shouldRememberColumns) {
                storeColumnVisibility(
                    newShownColumns,
                    newHiddenColumns,
                    columnList,
                    tableType,
                )
            }
        }

        const handleRestoreDefaultColumns = useCallback(() => {
            const newShownColumns = columnList.filter(
                column => column.initialShown,
            )
            const newHiddenColumns = columnList.filter(
                column => !column.initialShown,
            )

            shownColumns.value = newShownColumns
            hiddenColumns.value = newHiddenColumns

            handleStoreColumnsByType(newShownColumns, newHiddenColumns)

            handleRememberColumns(newShownColumns, newHiddenColumns, columnList)

            storeShouldRememberColumns(tableType, false)
        }, [shownColumns.value, hiddenColumns.value])

        const handleChangeColumnsViewed = useCallback(
            (newShownColumns, newHiddenColumns) => {
                const newMappedShownColumns = newShownColumns
                    .map(shownColumn => {
                        const column = columnList.find(
                            column => column.field === shownColumn.field,
                        )

                        if (!column) {
                            return null
                        }

                        return {
                            ...column,
                            initialShown: true,
                        }
                    })
                    .filter(column => column)

                const newMappedHiddenColumns = newHiddenColumns
                    .map(hiddenColumn => {
                        const column = columnList.find(
                            column => column.field === hiddenColumn.field,
                        )

                        if (!column) {
                            return null
                        }

                        return {
                            ...column,
                            initialShown: false,
                        }
                    })
                    .filter(column => column)

                shownColumns.value = newMappedShownColumns
                hiddenColumns.value = newMappedHiddenColumns

                handleStoreColumnsByType(newShownColumns, newHiddenColumns)

                handleRememberColumns(
                    newShownColumns,
                    newHiddenColumns,
                    tableColumnList,
                )
            },
            [shownColumns.value, hiddenColumns.value],
        )

        useEffect(() => {
            columnDataTableEvent.on(
                `${getColumnVisibilityChangeEventKey(
                    tableType,
                    columnEventTypes.COLUMN_VIEWED_CHANGE,
                )}`,
                (newShownColumns = [], newHiddenColumns = []) => {
                    handleChangeColumnsViewed(newShownColumns, newHiddenColumns)
                },
            )

            columnDataTableEvent.on(
                `${getColumnVisibilityChangeEventKey(
                    tableType,
                    columnEventTypes.COLUMN_VIEWED_RESTORE_DEFAULT,
                )}`,
                () => {
                    handleRestoreDefaultColumns()
                },
            )

            return () => {
                removeChangeColumnsViewedListener(
                    tableType,
                    columnEventTypes.COLUMN_VIEWED_CHANGE,
                )

                removeChangeColumnsViewedListener(
                    tableType,
                    columnEventTypes.COLUMN_VIEWED_RESTORE_DEFAULT,
                )
            }
        }, [])
        //#endregion

        //#regtion Value Interceptor
        const tableValue = useMemo(() => {
            if (!isArrayAndNotEmpty(dataList)) {
                if (tableLockRows.value?.length > 0) {
                    return [{id: `empty`, emptyRow: true}]
                }

                return []
            }

            const filteredValue = dataList.filter(row => {
                if (tableLockRows.value?.length < 1) {
                    return true
                }

                return !tableLockRows.value.some(lockRow => {
                    return lockRow[dataKey] === row[dataKey]
                })
            })

            if (filteredValue?.length < 1) {
                return [{id: `empty`, emptyRow: true}]
            }

            return [...filteredValue]
        }, [dataKey, dataList, tableLockRows.value])

        //#endregion
        //#region Context Menu when Lock Row mode is enabled
        const portionClickRef = useRef(null)
        const contextItemSelectedRef = useRef(null)
        const contextMenuRef = useRef(null)
        const contextMenuItems = [
            {
                label: (
                    <h6 className="text-gray-500 font-bold m-0 mb-1 section-header">
                        Actions
                    </h6>
                ),
            },
            {
                label: 'View Information',
                icon: (
                    <span className="p-menuitem-icon cursor-pointer">
                        <i className="pi pi-eye"></i>
                    </span>
                ),
                command: e => {
                    if (
                        !contextItemSelectedRef.current ||
                        !contextItemSelectedRef.current[dataKey] ||
                        !onDetailRowClick
                    ) {
                        return
                    }

                    onDetailRowClick(contextItemSelectedRef.current[dataKey])
                },
            },
            {
                label: (
                    <h6 className="text-gray-500 font-bold m-0 mb-1 section-header">
                        Visibilily
                    </h6>
                ),
            },
            {
                label: 'Close this menu',
                icon: 'pi pi-fw pi-times-circle',
                command: () => {
                    contextItemSelectedRef.current = null
                },
            },
        ]
        //#endregion

        const emptyRecordTemplate = () => {
            return (
                <div
                    className="flex justify-content-center align-items-center w-full"
                    style={{
                        minHeight: minHeightFullScreen
                            ? 'calc(100vh - 33rem)'
                            : '40vh',
                    }}>
                    {!isFetched ? (
                        <ProgressSpinner
                            style={{width: '50px', height: '50px'}}
                            strokeWidth="8"
                            animationDuration=".5s"
                        />
                    ) : (
                        <div className="flex flex-column  align-items-center">
                            <img className="h-15rem" src={noRecordImg} alt="" />
                            <h5>No results found!!</h5>
                        </div>
                    )}
                </div>
            )
        }

        return (
            <div
                className={`col-12 m-0 p-1 ${
                    !!isTealBackground ? ' header-teal-background' : ''
                }  `}>
                <DataTable
                    dataKey={dataKey || 'id'}
                    id={'styled-datatable'}
                    value={tableValue || []}
                    lazy={true}
                    sortMode="single"
                    sortField={pageable?.sortField || 'id'}
                    sortOrder={pageable?.sortOrder || -1}
                    onSort={e => {
                        if (onChangeSortColumn) {
                            onChangeSortColumn(e)
                        }
                    }}
                    className={`${
                        !!isTealBackground ? 'teal-background' : 'false'
                    } ${
                        minHeightFullScreen && 'styled-table-min-h-fullscreen'
                    }`}
                    resizableColumns={true}
                    rowHover
                    frozenValue={tableLockRows.value || []}
                    onRowSelect={e => {
                        if (!onRowSelect) {
                            return
                        }
                        if (e.type === 'row' && !!tableLock.value?.rowLock) {
                            contextItemSelectedRef.current = e?.data || {}

                            if (portionClickRef.current === 'lockRow') {
                                portionClickRef.current = 'clickRow'
                                return
                            } else {
                                contextMenuRef.current?.show(e.originalEvent)
                            }

                            portionClickRef.current = 'clickRow'
                            return
                        }

                        onRowSelect(e)
                    }}
                    stripedRows
                    {...tableScrollProps}
                    {...contextMenuProps}
                    {...rowExpansionProps}
                    {...props}>
                    {contextMenu && Object.keys(contextMenu).length > 0 && (
                        <Column
                            headerStyle={{width: '3rem'}}
                            header={
                                !contextMenu?.contextMenuSelection ? (
                                    <NotCurrentIcon width={22} height={22} />
                                ) : (
                                    <CurrentIcon width={22} height={22} />
                                )
                            }
                            bodyClassName={rowData =>
                                rowData.emptyRow ? 'datatable-empty-row' : ''
                            }
                            body={rowData => {
                                if (rowData.emptyRow) {
                                    return null
                                }
                                if (
                                    !contextMenu?.contextMenuSelection ||
                                    contextMenu?.contextMenuSelection[
                                        dataKey
                                    ] !== rowData[dataKey]
                                ) {
                                    return null
                                }

                                return (
                                    <i
                                        className="cursor-pointer pi pi-arrow-circle-right current-selected-row"
                                        onClick={() => {
                                            if (clearSelectedContextItem) {
                                                clearSelectedContextItem()
                                            }
                                        }}></i>
                                )
                            }}></Column>
                    )}
                    <Column
                        selectionMode="multiple"
                        bodyClassName={rowData =>
                            rowData.emptyRow ? 'datatable-empty-row' : ''
                        }
                        headerStyle={{width: '3rem'}}></Column>
                    {!!expansionOption?.hasExpansion && (
                        <Column
                            bodyClassName={rowData =>
                                rowData.emptyRow ? 'datatable-empty-row' : ''
                            }
                            expander={rowData =>
                                expansionOption?.expansionKey
                                    ? rowData[expansionOption?.expansionKey]
                                          ?.length > 0
                                    : false
                            }
                            style={{width: '3rem'}}
                            header={<TreeIcon width={22} height={22} />}
                        />
                    )}
                    {columns}
                    {/* Action column */}
                    {tableActions && tableActions?.length > 0 && (
                        <Column
                            style={{width: '5rem'}}
                            bodyClassName={rowData =>
                                rowData.emptyRow ? 'datatable-empty-row' : ''
                            }
                            header={
                                <div className="flex justify-content-between align-items-center">
                                    <div className="capitalized">Action</div>
                                    <i
                                        className="pi pi-cog ml-2"
                                        style={{fontSize: '20px'}}></i>
                                </div>
                            }
                            body={renderActionTemplate}></Column>
                    )}
                    {!!tableLock.value?.rowLock && (
                        <Column
                            style={{width: '5rem'}}
                            header={<LockRow width={22} height={22} />}
                            bodyClassName={rowData =>
                                rowData.emptyRow ? 'datatable-empty-row' : ''
                            }
                            body={rowData => {
                                if (rowData.emptyRow) {
                                    return
                                }
                                return (
                                    <i
                                        className={`pi cursor-pointer lock-icon p-2 ${
                                            tableLockRows.value?.includes(
                                                rowData,
                                            )
                                                ? 'pi-lock'
                                                : 'pi-lock-open'
                                        }`}
                                        onClick={() => {
                                            portionClickRef.current = 'lockRow'

                                            tableLockRows.value?.includes(
                                                rowData,
                                            )
                                                ? handleUnlockRows(rowData)
                                                : handleLockRows(rowData)
                                        }}></i>
                                )
                            }}></Column>
                    )}
                </DataTable>
                <StyledTablePaginator />

                <ContextMenu
                    model={contextMenuItems}
                    ref={contextMenuRef}
                    onHide={() => {}}
                />

                <ConfirmPopup />
                <ConfirmDialog />
            </div>
        )
    },
)

export default StyledDataTable
